import React from 'react';
import { Layout } from '../components/layout/Layout';
import { SEO } from '../components/shared/SEO';
import { Link } from 'react-router-dom';

const Section = ({ title, children }) => (
  <div className="mb-10">
    <h2 className="text-xl font-black uppercase tracking-wide text-white mb-4 pb-2 border-b border-slate-700">
      {title}
    </h2>
    <div className="text-slate-300 text-sm leading-relaxed space-y-3">
      {children}
    </div>
  </div>
);

const PrivacyPage = () => {
  const updated = 'April 13, 2026';

  return (
    <Layout>
      <SEO
        title="Privacy Policy | AAA Irrigation Service"
        description="Privacy policy for AAA Irrigation Service — how we collect, use, and protect your personal information."
      />

      <div className="bg-[#0f172a] min-h-screen py-16 px-4">
        <div className="max-w-3xl mx-auto">

          <h1 className="text-3xl font-black uppercase italic tracking-wide text-white mb-2">
            Privacy Policy
          </h1>
          <p className="text-slate-500 text-sm mb-10">Last updated: {updated}</p>

          <Section title="Overview">
            <p>
              AAA Irrigation Service LLC ("Company," "we," "us," "our") respects your privacy.
              This policy explains what personal information we collect, how we use it, and your
              rights regarding that information. By using our website or services, you agree to
              the practices described here.
            </p>
          </Section>

          <Section title="Information We Collect">
            <p>We may collect the following types of personally identifiable information (PII):</p>
            <ul className="list-disc list-inside space-y-1 pl-2">
              <li>Full name</li>
              <li>Phone number (including mobile)</li>
              <li>Email address</li>
              <li>Service address / property location</li>
              <li>Service history and appointment records</li>
              <li>Communications with our team (calls, texts, emails)</li>
            </ul>
            <p>
              We collect this information when you contact us, schedule a service, submit a web
              form, or interact with our AI phone assistant.
            </p>
          </Section>

          <Section title="How We Use Your Information">
            <p>We use the information we collect to:</p>
            <ul className="list-disc list-inside space-y-1 pl-2">
              <li>Schedule and dispatch service appointments</li>
              <li>Send appointment confirmations, reminders, and status updates</li>
              <li>Deliver estimates, invoices, and service records</li>
              <li>Respond to inquiries and provide customer support</li>
              <li>Improve our services and communications</li>
              <li>Comply with legal obligations</li>
            </ul>
          </Section>

          <Section title="SMS / Text Messaging Data">
            <p>
              If you provide your mobile phone number and consent to receive text messages, we
              will use that number solely to send service-related communications including
              appointment reminders, technician dispatch notifications, estimate delivery, and
              two-way support conversations.
            </p>
            <p className="text-white font-semibold bg-slate-800 border border-slate-600 rounded-lg px-4 py-3">
              No mobile phone numbers or SMS consent information will be shared with third
              parties or affiliates for marketing or promotional purposes. All the above
              categories exclude text messaging originator opt-in data and consent; this
              information will not be shared with any third parties.
            </p>
            <p>
              We will not sell, share, or rent your SMS opt-in information to any third party
              for any reason other than to deliver the specific services associated with the
              campaign. However, we may share your personal data, including your SMS opt-in or
              consent status, with third parties that assist us in providing messaging services,
              such as platform providers, phone carriers, and other vendors involved in
              delivering text messages.
            </p>
            <p>
              You may opt out of SMS messages at any time by replying{' '}
              <span className="font-mono text-[#ea580c]">STOP</span> to any message.
              Reply <span className="font-mono text-[#ea580c]">HELP</span> for assistance.
              Standard message and data rates may apply.
            </p>
          </Section>

          <Section title="Sharing Your Information">
            <p>
              We do not sell, rent, or trade your personal information. We may share information
              only in the following limited circumstances:
            </p>
            <ul className="list-disc list-inside space-y-1 pl-2">
              <li>
                <strong className="text-white">Service providers:</strong> Third-party vendors
                (scheduling, payment processing, communications platforms) that assist us in
                operating our business — bound by confidentiality obligations
              </li>
              <li>
                <strong className="text-white">Legal requirements:</strong> When required by law,
                court order, or to protect the rights and safety of our customers or business
              </li>
            </ul>
            <p>
              We do not share personal information with third parties for their own marketing purposes.
            </p>
          </Section>

          <Section title="Data Retention">
            <p>
              We retain customer records for a minimum of <strong className="text-white">three (3) years</strong> in
              accordance with TCEQ recordkeeping requirements under 30 TAC §344.38, which requires
              archival copies of all documents provided to irrigation system owners to be maintained
              and made available to TCEQ or a local regulatory agency within ten business days of
              a request. Records may be retained longer as necessary to fulfill legal obligations
              or resolve disputes. You may request deletion of non-regulated information at any
              time by contacting us.
            </p>
          </Section>

          <Section title="Your Rights">
            <p>You have the right to:</p>
            <ul className="list-disc list-inside space-y-1 pl-2">
              <li>Request a copy of the personal information we hold about you</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your personal information</li>
              <li>Opt out of SMS communications at any time (reply STOP)</li>
              <li>Opt out of email communications at any time</li>
            </ul>
            <p>
              To exercise any of these rights, contact us at{' '}
              <a href="mailto:info@aaairrigationservice.com" className="text-[#1b7abf] hover:underline">
                info@aaairrigationservice.com
              </a>.
            </p>
          </Section>

          <Section title="Cookies & Website Analytics">
            <p>
              Our website may use standard analytics tools to understand how visitors interact
              with our pages. We do not use tracking cookies for advertising purposes. You may
              disable cookies in your browser settings at any time.
            </p>
          </Section>

          <Section title="Children's Privacy">
            <p>
              Our services are not directed to children under 13. We do not knowingly collect
              personal information from minors. If you believe we have inadvertently collected
              such information, please contact us immediately.
            </p>
          </Section>

          <Section title="Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. Changes will be posted on
              this page with an updated date. Continued use of our services after changes
              constitutes acceptance of the revised policy.
            </p>
          </Section>

          <Section title="Contact Us">
            <p>
              AAA Irrigation Service LLC<br />
              Plano / Allen, TX<br />
              Phone:{' '}
              <a href="tel:4697513567" className="text-[#1b7abf] hover:underline">(469) 751-3567</a>
              <br />
              Email:{' '}
              <a href="mailto:info@aaairrigationservice.com" className="text-[#1b7abf] hover:underline">
                info@aaairrigationservice.com
              </a>
            </p>
            <p>
              View our{' '}
              <Link to="/terms" className="text-[#1b7abf] hover:underline">Terms &amp; Conditions</Link>
              {' '}for SMS messaging terms.
            </p>
          </Section>

        </div>
      </div>
    </Layout>
  );
};

export default PrivacyPage;
